import { z } from 'zod';
import { PsychologicalQuestionResponseType } from '@/apolloClient.types';

export const scoringSchema = z.object({
    score: z.number(),
    label: z.string(),
});

export type ScoringSchema = z.infer<typeof scoringSchema>;

export const likertScaleResponseTypeSchema = z.object({
    responseType: z.enum(PsychologicalQuestionResponseType),
    scoring: z.array(scoringSchema),
});

export type LikertScaleResponseTypeSchema = z.infer<typeof likertScaleResponseTypeSchema>;

export const scaleLabelSchema = z.object({
    label: z.string().min(1, 'Label is required'),
    description: z.string().optional().nullable(),
    value: z.number(),
});

export const ratingScaleResponseTypeSchema = z
    .object({
        responseType: z.enum(PsychologicalQuestionResponseType),
        minimumValue: z.number().gte(0, 'Minimum value must be greater than or equal to 0'),
        maximumValue: z.number(),
        increment: z.number().gt(0, 'Increment must be greater than 0'),
        scaleLabels: z.array(scaleLabelSchema),
    })
    .refine(data => data.minimumValue <= data.maximumValue, {
        message: 'Minimum value must be less than or equal to maximum value',
        path: ['minimumValue'],
    });

export type RatingScaleResponseTypeSchema = z.infer<typeof ratingScaleResponseTypeSchema>;

export const responseTypeSchema = z.object({
    likertScale: likertScaleResponseTypeSchema.optional(),
    ratingScale: ratingScaleResponseTypeSchema.optional(),
});

export type ResponseTypeSchema = z.infer<typeof responseTypeSchema>;

export const defaultLikertScaleResponseTypeOne: LikertScaleResponseTypeSchema = {
    responseType: PsychologicalQuestionResponseType.Likert,
    scoring: [
        {
            score: 1,
            label: 'Almost Never',
        },

        {
            score: 2,
            label: 'Rarely',
        },

        {
            score: 3,
            label: 'Once In A While',
        },
        {
            score: 4,
            label: 'Occasionally',
        },
        {
            score: 5,
            label: 'Usually',
        },
        {
            score: 6,
            label: 'Very Frequently',
        },
        {
            score: 7,
            label: 'Almost Always',
        },
    ],
};

export const defaultLikertScaleResponseTypeTwo: LikertScaleResponseTypeSchema = {
    responseType: PsychologicalQuestionResponseType.Likert,
    scoring: [
        {
            score: 1,
            label: 'Strongly Disagree',
        },
        {
            score: 2,
            label: 'Disagree',
        },
        {
            score: 3,
            label: 'Neutral',
        },

        {
            score: 4,
            label: 'Agree',
        },
        {
            score: 5,
            label: 'Strongly Agree',
        },
    ],
};

export const defaultRatingScaleResponseType: RatingScaleResponseTypeSchema = {
    responseType: PsychologicalQuestionResponseType.Rating,
    minimumValue: 0,
    maximumValue: 100,
    increment: 10,
    scaleLabels: [
        {
            label: 'Totally spent, nothing in reserve',
            description: '',
            value: 0,
        },
        {
            label: 'Totally spent, nothing in reserve',
            description: '',
            value: 10,
        },
        {
            label: 'Somewhat depleted, but something in reserve',
            description: '',
            value: 20,
        },
        {
            label: 'Somewhat depleted, but something in reserve',
            description: '',
            value: 30,
        },
        {
            label: 'Somewhat depleted, but something in reserve',
            description: '',
            value: 40,
        },
        {
            label: 'Somewhat depleted, but something in reserve',
            description: '',
            value: 50,
        },
        {
            label: 'Somewhat depleted, but something in reserve',
            description: '',
            value: 60,
        },
        {
            label: 'Somewhat depleted, but something in reserve',
            description: '',
            value: 70,
        },
        {
            label: 'Somewhat depleted, but something in reserve',
            description: '',
            value: 80,
        },
        {
            label: 'Somewhat depleted, but something in reserve',
            description: '',
            value: 90,
        },
        {
            label: 'Fully ready with plenty in reserve',
            description: '',
            value: 100,
        },
    ],
};

export const defaultRatingScaleResponseTypeTwo: RatingScaleResponseTypeSchema = {
    responseType: PsychologicalQuestionResponseType.Rating,
    minimumValue: 1,
    maximumValue: 5,
    increment: 1,
    scaleLabels: [
        {
            label: 'Almost Never',
            description: '',
            value: 1,
        },
        {
            label: 'Rarely',
            description: '',
            value: 2,
        },
        {
            label: 'Sometimes',
            description: '',
            value: 3,
        },
        {
            label: 'Often',
            description: '',
            value: 4,
        },
        {
            label: 'Almost Always',
            description: '',
            value: 5,
        },
    ],
};
